/**
 * DEMO-ONLY sessionStorage state for Sales Admin (price lists and catalogs).
 */
import {
  SalesAdminCatalog,
  SalesAdminManagedPriceList,
  SalesAdminPriceRule,
} from '../../presidio-sales-admin/models/presidio-sales-admin.model';
import { getPresidioMockActiveCustomerNo, getPresidioMockActiveLogin } from './presidio-mock-session';

const KEY = 'presidio-mock-sales-admin-state-v6';

interface SalesAdminMockState {
  managedListsByCustomer: Record<string, SalesAdminManagedPriceList[]>;
  priceRulesByCustomer: Record<string, SalesAdminPriceRule[]>;
  catalogsByCustomer: Record<string, SalesAdminCatalog[]>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function actor(): string {
  return getPresidioMockActiveLogin() || 'sales.admin';
}

/** Optional per-request customer from the sales-admin URL (affiliate filter). */
let requestCustomerNo: string | undefined;

export function withSalesAdminRequestCustomer<T>(customer: string, fn: () => T): T {
  const previous = requestCustomerNo;
  requestCustomerNo = (customer || '').trim() || undefined;
  try {
    return fn();
  } finally {
    requestCustomerNo = previous;
  }
}

function customerNo(): string {
  return requestCustomerNo || getPresidioMockActiveCustomerNo() || 'PRESIDIO-MOCK';
}

function emptyState(): SalesAdminMockState {
  return {
    managedListsByCustomer: {},
    priceRulesByCustomer: {},
    catalogsByCustomer: {},
  };
}

function priorityListId(customer: string, priority: 1 | 2 | 3 | 4): string {
  return `PL-${customer}-P${priority}`;
}

function seedFor(customer: string): SalesAdminMockState {
  const state = emptyState();
  // Each account has four ICM price lists (P1–P4). Names are internal only.
  state.managedListsByCustomer[customer] = (
    [
      [1, 'P1 — SKU flat'],
      [2, 'P2 — SKU percent'],
      [3, 'P3 — Manufacturer percent'],
      [4, 'P4 — Custom catalog percent'],
    ] as const
  ).map(([priority, name]) => ({
    id: priorityListId(customer, priority),
    customerNo: customer,
    name,
    priority,
    updatedAt: nowIso(),
    updatedBy: 'seed',
  }));
  // Small custom catalog used only for the P4 example (one SKU).
  const p4CatalogId = `CAT-${customer}-P4`;
  state.catalogsByCustomer[customer] = [
    {
      id: p4CatalogId,
      customerNo: customer,
      name: 'Accessories promo',
      description: 'P4 catalog pricing example',
      skus: ['AP8858'],
      updatedAt: nowIso(),
      updatedBy: 'seed',
    },
  ];
  /**
   * One seed assignment per priority. Targets are disjoint so Overridden SKUs
   * shows the same four examples as Assign pricing (P1–P4).
   */
  state.priceRulesByCustomer[customer] = [
    {
      id: `PR-${customer}-P1`,
      customerNo: customer,
      priceListId: priorityListId(customer, 1),
      name: 'CISCO-C9300 flat',
      level: 'sku_flat',
      overrideType: 'flat',
      sku: 'CISCO-C9300',
      skus: ['CISCO-C9300'],
      flatPrice: 7200,
      active: true,
      updatedAt: nowIso(),
      updatedBy: 'seed',
    },
    {
      id: `PR-${customer}-P2`,
      customerNo: customer,
      priceListId: priorityListId(customer, 2),
      name: 'PA-3220 12%',
      level: 'sku_percent',
      overrideType: 'percent',
      sku: 'PA-3220',
      skus: ['PA-3220'],
      percentOff: 12,
      active: true,
      updatedAt: nowIso(),
      updatedBy: 'seed',
    },
    {
      id: `PR-${customer}-P3`,
      customerNo: customer,
      priceListId: priorityListId(customer, 3),
      name: 'Anker 8%',
      level: 'manufacturer',
      overrideType: 'percent',
      manufacturer: 'Anker',
      percentOff: 8,
      active: true,
      updatedAt: nowIso(),
      updatedBy: 'seed',
    },
    {
      id: `PR-${customer}-P4`,
      customerNo: customer,
      priceListId: priorityListId(customer, 4),
      name: 'Accessories promo 5%',
      level: 'catalog',
      overrideType: 'percent',
      catalogId: p4CatalogId,
      percentOff: 5,
      active: true,
      updatedAt: nowIso(),
      updatedBy: 'seed',
    },
  ];
  return state;
}

function readState(): SalesAdminMockState {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) {
      const seeded = seedFor(customerNo());
      writeState(seeded);
      return seeded;
    }
    return { ...emptyState(), ...JSON.parse(raw) } as SalesAdminMockState;
  } catch {
    return seedFor(customerNo());
  }
}

function writeState(state: SalesAdminMockState): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

function hasPriorityBuckets(lists: SalesAdminManagedPriceList[] | undefined): boolean {
  if (!lists || lists.length < 4) {
    return false;
  }
  const priorities = new Set(lists.map(l => l.priority));
  return priorities.has(1) && priorities.has(2) && priorities.has(3) && priorities.has(4);
}

function ensureCustomerBuckets(state: SalesAdminMockState, customer: string): void {
  if (!state.managedListsByCustomer) {
    state.managedListsByCustomer = {};
  }
  const needsSeed =
    !state.priceRulesByCustomer[customer] ||
    !state.managedListsByCustomer[customer]?.length ||
    !hasPriorityBuckets(state.managedListsByCustomer[customer]);
  if (needsSeed) {
    const seeded = seedFor(customer);
    state.managedListsByCustomer[customer] = seeded.managedListsByCustomer[customer];
    // Keep existing rules only when they already target P1–P4 ids; otherwise reseed.
    const existing = state.priceRulesByCustomer[customer] || [];
    const pIds = new Set(seeded.managedListsByCustomer[customer].map(l => l.id));
    const keep = existing.filter(r => r.priceListId && pIds.has(r.priceListId));
    state.priceRulesByCustomer[customer] = keep.length
      ? keep
      : seeded.priceRulesByCustomer[customer];
    if (!state.catalogsByCustomer[customer]?.length) {
      state.catalogsByCustomer[customer] = seeded.catalogsByCustomer[customer];
    }
  }
}

export function listSalesAdminManagedPriceLists(): SalesAdminManagedPriceList[] {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  writeState(state);
  return [...(state.managedListsByCustomer[customer] || [])];
}

/** Prototype helper — accounts are seeded with P1–P4; UI does not create lists. */
export function createSalesAdminManagedPriceList(
  name: string,
  priority: 1 | 2 | 3 | 4 = 1
): SalesAdminManagedPriceList {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  const next: SalesAdminManagedPriceList = {
    id: priorityListId(customer, priority),
    customerNo: customer,
    name: name.trim(),
    priority,
    updatedAt: nowIso(),
    updatedBy: actor(),
  };
  const others = (state.managedListsByCustomer[customer] || []).filter(l => l.priority !== priority);
  state.managedListsByCustomer[customer] = [...others, next].sort((a, b) => a.priority - b.priority);
  writeState(state);
  return next;
}

export function listSalesAdminPriceRules(priceListId?: string): SalesAdminPriceRule[] {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  writeState(state);
  const all = [...(state.priceRulesByCustomer[customer] || [])];
  if (!priceListId) {
    return all;
  }
  return all.filter(r => r.priceListId === priceListId);
}

export function upsertSalesAdminPriceRule(rule: SalesAdminPriceRule, isNew: boolean): SalesAdminPriceRule {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  const lists = state.managedListsByCustomer[customer] || [];
  const priceListId = rule.priceListId || lists[0]?.id || '';
  const list = state.priceRulesByCustomer[customer] || [];
  const next = {
    ...rule,
    priceListId,
    customerNo: customer,
    updatedAt: nowIso(),
    updatedBy: actor(),
  };
  // Newest first so same-priority overlaps prefer the most recently saved rule.
  state.priceRulesByCustomer[customer] = isNew
    ? [next, ...list]
    : [next, ...list.filter(r => r.id !== next.id)];
  writeState(state);
  return next;
}

export function deleteSalesAdminPriceRule(id: string): boolean {
  const customer = customerNo();
  const state = readState();
  const before = state.priceRulesByCustomer[customer]?.length || 0;
  state.priceRulesByCustomer[customer] = (state.priceRulesByCustomer[customer] || []).filter(r => r.id !== id);
  writeState(state);
  return (state.priceRulesByCustomer[customer]?.length || 0) < before;
}

export function listSalesAdminCatalogs(): SalesAdminCatalog[] {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  writeState(state);
  return [...(state.catalogsByCustomer[customer] || [])];
}

export function upsertSalesAdminCatalog(catalog: SalesAdminCatalog, isNew: boolean): SalesAdminCatalog {
  const customer = customerNo();
  const state = readState();
  ensureCustomerBuckets(state, customer);
  const list = state.catalogsByCustomer[customer] || [];
  const next = { ...catalog, customerNo: customer, updatedAt: nowIso(), updatedBy: actor() };
  state.catalogsByCustomer[customer] = isNew ? [next, ...list] : list.map(c => (c.id === next.id ? next : c));
  writeState(state);
  return next;
}

export function deleteSalesAdminCatalog(id: string): boolean {
  const customer = customerNo();
  const state = readState();
  const before = state.catalogsByCustomer[customer]?.length || 0;
  state.catalogsByCustomer[customer] = (state.catalogsByCustomer[customer] || []).filter(c => c.id !== id);
  writeState(state);
  return (state.catalogsByCustomer[customer]?.length || 0) < before;
}

export function newSalesAdminId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}
